import React, { useState } from 'react';
import { BookOpen, ArrowRight, ArrowLeft, Upload, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { CertifiedNetworkApplication, uploadDocuments } from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step5TrainingProps {
  data: Partial<CertifiedNetworkApplication>;
  applicationId: string | null;
  onComplete: (data: any) => void;
  onBack: () => void;
}

const Step5Training: React.FC<Step5TrainingProps> = ({ data, applicationId, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    training: data.training || {
      hasFrequentato: false,
      corsiFrequentati: '',
      documenti: []
    }
  });

  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      
      // Validazione: max 5 file, max 5MB ciascuno
      if (files.length + selectedFiles.length > 5) {
        toast.error('Puoi caricare massimo 5 documenti');
        return;
      }

      const invalidFiles = files.filter(f => f.size > 5 * 1024 * 1024);
      if (invalidFiles.length > 0) {
        toast.error('Ogni file deve essere massimo 5MB');
        return;
      }

      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!applicationId) {
      toast.error('ID domanda non trovato');
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error('Seleziona almeno un file');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadDocuments(applicationId, selectedFiles);
      if (response.success) {
        toast.success('Documenti caricati con successo');
        setFormData(prev => ({
          ...prev,
          training: {
            ...prev.training,
            documenti: [...prev.training.documenti, ...response.documents]
          }
        }));
        setSelectedFiles([]);
      }
    } catch (error: any) {
      toast.error('Errore durante il caricamento');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900/50 rounded-full mb-4">
          <BookOpen className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Formazione e Certificazioni
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Indica i corsi frequentati e carica gli attestati.
        </p>
      </div>

      {/* Sezione 10: Formazione */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          10. Formazione e Certificazioni
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="hasFrequentato"
            checked={formData.training.hasFrequentato}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              training: { ...prev.training, hasFrequentato: e.target.checked }
            }))}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="hasFrequentato" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Il titolare/amministratore o un dipendente ha frequentato corsi presso il Centro Studi "Cesare Ferrari"
          </label>
        </div>

        {formData.training.hasFrequentato && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Elencare i corsi frequentati e le certificazioni ottenute
              </label>
              <textarea
                rows={4}
                value={formData.training.corsiFrequentati}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  training: { ...prev.training, corsiFrequentati: e.target.value }
                }))}
                placeholder="es. Corso GDPR 2023, Certificazione Cybersecurity 2024..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Upload Documenti */}
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <div className="text-center mb-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  Carica Attestati e Certificazioni
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  PDF, JPG, PNG - Max 5 file da 5MB ciascuno
                </p>
              </div>

              <input
                type="file"
                id="fileUpload"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
              />

              <label
                htmlFor="fileUpload"
                className="block w-full text-center px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Seleziona File
              </label>

              {/* File Selezionati */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">File selezionati:</p>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <LoadingButton
                    type="button"
                    onClick={handleUpload}
                    loading={uploading}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
                  >
                    Carica Documenti
                  </LoadingButton>
                </div>
              )}

              {/* Documenti Caricati */}
              {formData.training.documenti.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Documenti caricati:</p>
                  {formData.training.documenti.map((doc: any, index: number) => (
                    <div key={index} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 p-2 rounded">
                      <span className="text-sm text-green-700 dark:text-green-300">{doc.originalName}</span>
                      <span className="text-xs text-green-600 dark:text-green-400">✓ Caricato</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          type="submit"
          className="flex items-center gap-2 bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </LoadingButton>
      </div>
    </form>
  );
};

export default Step5Training;

